import { MoreVertical, Pencil, ScrollText } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, Navigate, NavLink, Route, Routes, useParams } from 'react-router-dom'
import { api, errorText } from '../../api.ts'
import { useCompany, useCompanyCache } from '../../company.ts'
import type { AppRef, Company, CompanyDetail, OffboardCheck } from '../../types.ts'
import { AppMark, Badge, byKind, itemName, Modal, CompanyMark, Field, Loading, Notice, when } from '../../ui.tsx'
import { CompanySettings } from '../CompanySettings.tsx'
import { Jobs } from '../Jobs.tsx'
import { Users } from '../Users.tsx'
import { JobSources } from './JobSources.tsx'
import { statusTone } from './Companies.tsx'
import { Button } from '../../components/Button.tsx'
import { Dropdown, DropdownDivider, DropdownItem } from '../../components/Dropdown.tsx'

/** An Icefield administrator's support view of one company (PLAN.md 1.6). Opening it is
 *  recorded in the company's activity log, every time. */
export function CompanyView() {
  const companyId = useParams().companyId!
  const detail = useCompany(companyId)
  const [dialog, setDialog] = useState<'edit' | 'logs' | null>(null)
  if (detail.isPending) return <Loading />
  if (detail.isError) return <Notice>{errorText(detail.error)}</Notice>
  const { company } = detail.data
  // Absolute: inside a /* route, React Router resolves relative links against the whole
  // current address, so a relative one from a tab would nest under that tab.
  const base = `/admin/companies/${company.id}`
  const logo = company.logo ? `/dashboard/api/companies/${company.id}/logo?v=${company.logo.sha256.slice(0, 12)}` : null

  return (
    <section>
      <p className="crumbs"><Link to="/admin/companies">Companies</Link> / {company.slug}</p>
      <div className="page-head company-line">
        <CompanyMark name={company.name} logo={logo} size={48} />
        <h1>{company.name}</h1>
        <Badge tone={statusTone(company.status)}>{company.status}</Badge>
        <span className="company-line-menu">
          <Dropdown
            align="end"
            trigger={({ open, toggle }) => (
              <button type="button" className="icon-button icon-button-plain" onClick={toggle} aria-expanded={open} aria-haspopup="menu" aria-label={`Actions for ${company.name}`}>
                <MoreVertical aria-hidden="true" />
              </button>
            )}
          >
            <DropdownItem icon={Pencil} onClick={() => setDialog('edit')}>Edit company</DropdownItem>
            <DropdownDivider />
            <DropdownItem icon={ScrollText} onClick={() => setDialog('logs')}>Logs</DropdownItem>
          </Dropdown>
        </span>
      </div>
      <nav className="tabs" aria-label="Company">
        <NavLink to={base} end>Users</NavLink>
        <NavLink to={`${base}/jobs`}>Jobs</NavLink>
        <NavLink to={`${base}/job-sources`}>Job sources</NavLink>
        <NavLink to={`${base}/apps`}>Apps</NavLink>
        <NavLink to={`${base}/signin`}>Sign-in</NavLink>
        <NavLink to={`${base}/offboard`}>Offboard</NavLink>
      </nav>
      <Routes>
        <Route index element={<Users companyId={companyId} />} />
        <Route path="jobs" element={<Jobs companyId={companyId} />} />
        <Route path="job-sources" element={<JobSources companyId={companyId} />} />
        <Route path="apps" element={<Apps detail={detail.data} />} />
        <Route path="signin" element={<><SignIn company={company} /><Consent detail={detail.data} /></>} />
        <Route path="offboard" element={<Offboard detail={detail.data} />} />
        {/* The Company and Activity tabs became the Edit company and Logs dialogs. */}
        <Route path="*" element={<Navigate to={base} replace />} />
      </Routes>
      <Modal title="Edit company" wide open={dialog === 'edit'} onClose={() => setDialog(null)}>
        <CompanySettings companyId={companyId} />
      </Modal>
      <Modal title="Logs" wide open={dialog === 'logs'} onClose={() => setDialog(null)}>
        <Activity detail={detail.data} />
      </Modal>
    </section>
  )
}

/** The module switch (PLAN.md 1.10): which apps and MCP servers this company has. */
function Apps({ detail }: { detail: CompanyDetail }) {
  const cache = useCompanyCache(detail.company.id)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const on = new Set(detail.apps.map((a) => a.appId))

  const toggle = async (app: AppRef, enabled: boolean) => {
    setBusy(true)
    setError(null)
    try {
      const { apps } = await api.put<{ apps: AppRef[] }>(`/admin/companies/${detail.company.id}/apps/${app.appId}`, { enabled })
      cache.patch((d) => ({ ...d, apps }))
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <h2>Apps</h2>
      <p className="muted">
        An app or MCP server that's off doesn't appear on the company's dashboard, and nobody in the company can sign in to it.
        Switching an MCP server off disconnects everyone from it at once.
      </p>
      <Notice>{error}</Notice>
      {byKind(detail.allApps ?? []).map((group) => (
        <div key={group.kind}>
          <h3 className="kind-label">{group.label}</h3>
          {group.items.map((app) => (
            <label key={app.appId} className="check">
              <input type="checkbox" checked={on.has(app.appId)} disabled={busy} onChange={(e) => toggle(app, e.target.checked)} />
              <AppMark appId={app.appId} name={app.name} kind={app.kind} size={28} />
              {itemName(app)}
            </label>
          ))}
        </div>
      ))}
    </div>
  )
}

function SignIn({ company }: { company: Company }) {
  const cache = useCompanyCache(company.id)
  const [entraTenantId, setEntraTenantId] = useState(company.signIn.entraTenantId ?? '')
  const [allowPersonalMs, setAllowPersonalMs] = useState(company.signIn.allowPersonalMs)
  const [status, setStatus] = useState<'active' | 'suspended'>(company.status === 'suspended' ? 'suspended' : 'active')
  const [message, setMessage] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setMessage(null)
    try {
      const updated = await api.patch<Company>(`/companies/${company.id}`, {
        version: company.version,
        signIn: { entraTenantId: entraTenantId.trim() || null, allowPersonalMs },
        status,
      })
      cache.putCompany(updated)
      setMessage({ kind: 'ok', text: 'Saved.' })
    } catch (err) {
      setMessage({ kind: 'error', text: errorText(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="card form" onSubmit={submit}>
      <h2>Sign-in</h2>
      <Notice kind={message?.kind}>{message?.text}</Notice>
      <Field label="Provider"><input value="Microsoft" disabled /></Field>
      <Field label="Entra tenant id" hint="Recorded for the company. Sign-in doesn't enforce it yet, matching Crew Dispatch.">
        <input value={entraTenantId} onChange={(e) => setEntraTenantId(e.target.value)} />
      </Field>
      <label className="check">
        <input type="checkbox" checked={allowPersonalMs} onChange={(e) => setAllowPersonalMs(e.target.checked)} />
        Allow personal Microsoft accounts
      </label>
      <Field label="Status" hint="A suspended company's users can't sign in. To end a company's use of Icefield for good, offboard it.">
        <select value={status} onChange={(e) => setStatus(e.target.value as 'active' | 'suspended')}>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </Field>
      <div className="form-actions">
        <Button type="submit" disabled={busy}>Save</Button>
      </div>
    </form>
  )
}

const CONSENT_OUTCOMES = {
  granted: { tone: 'ok', label: 'Granted' },
  unverified: { tone: 'warn', label: 'Unverified' },
  declined: { tone: 'bad', label: 'Declined' },
} as const

/** Microsoft admin consent to ICE's registration (docs/ice-admin-consent.md): the latest
 *  outcome, and the link to send the company's IT. */
function Consent({ detail }: { detail: CompanyDetail }) {
  const [copied, setCopied] = useState(false)
  const consent = detail.consent ?? null
  const granted = detail.consentGranted ?? null
  const link = detail.consentLink ?? null

  const copy = async () => {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
    } catch {
      // The link is in the box beside the button, to copy by hand.
    }
  }

  return (
    <div className="card form">
      <h2>Microsoft consent</h2>
      <p className="muted">
        A company whose Microsoft tenant restricts user consent needs its IT to grant admin consent to Icefield's apps
        once, or everyone sees an approval request at their first sign-in.
      </p>
      <p>
        {granted ? (
          <>
            <Badge tone="ok">Granted</Badge> {when(granted.at)}
            {granted.tenant && <span className="muted small"> · tenant {granted.tenant}</span>}
          </>
        ) : (
          !consent && 'None recorded.'
        )}
      </p>
      {/* The latest attempt, when it isn't the grant above: declined, or not confirmed. */}
      {consent && consent.outcome !== 'granted' && (
        <p className={granted ? 'small' : undefined}>
          {granted && <span className="muted">Latest attempt: </span>}
          <Badge tone={CONSENT_OUTCOMES[consent.outcome].tone}>{CONSENT_OUTCOMES[consent.outcome].label}</Badge>{' '}
          {when(consent.at)}
          {consent.tenant && <span className="muted small"> · tenant {consent.tenant}</span>}
          {consent.error && <span className="muted small"> · {consent.error}</span>}
        </p>
      )}
      {link ? (
        <>
          <Field label="Consent link" hint="Send it to the company's IT. Only an administrator of their Microsoft tenant can accept.">
            <input value={link} readOnly onFocus={(e) => e.target.select()} />
          </Field>
          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={copy}>{copied ? 'Copied' : 'Copy consent link'}</Button>
          </div>
        </>
      ) : (
        <p className="muted small">Add a domain or an Entra tenant id to make a consent link.</p>
      )}
    </div>
  )
}

const EVENT_NAMES: Record<string, string> = {
  sign_in: 'Signed in',
  sign_in_refused: 'Sign-in refused',
  sign_out: 'Signed out',
  app_access_granted: 'App access granted',
  app_access_removed: 'App access removed',
  company_app_enabled: 'App switched on',
  company_app_disabled: 'App switched off',
  company_access_removed: 'Company offboarded',
  support_view: 'Icefield support view',
  tenant_consent: 'Microsoft consent',
}

function Activity({ detail }: { detail: CompanyDetail }) {
  const events = detail.accessLog ?? []
  return (
    <div>
      <p className="muted">The last 50 access events. The full log is kept, and never purged.</p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>When</th><th>What</th><th>Who</th><th>App</th><th>By</th></tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td className="nowrap">{when(e.at)}</td>
                <td>{EVENT_NAMES[e.event] ?? e.event}</td>
                <td>{e.personName ?? '—'}</td>
                <td>{e.appId ?? (e.detail.surface === 'dashboard' ? 'Dashboard' : '—')}</td>
                <td className="small">{e.actorName ?? e.actorLabel ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Offboarding (SOP §9.10): remove the company's access, then check nobody can sign in. */
function Offboard({ detail }: { detail: CompanyDetail }) {
  const cache = useCompanyCache(detail.company.id)
  const [confirm, setConfirm] = useState('')
  const [result, setResult] = useState<OffboardCheck | null>(detail.offboarding ?? null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const { company } = detail

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      setResult(await api.post<OffboardCheck>(`/admin/companies/${company.id}/offboard`, { confirm }))
      await cache.reload()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <h2>Offboard</h2>
      {result ? (
        <>
          <Notice kind={result.verified ? 'ok' : 'error'}>
            {result.verified
              ? 'Verified: the company is offboarded, no app is on for it, and nobody in it can sign in.'
              : 'Not verified. Something still gives this company access. Check the details below.'}
          </Notice>
          <ul className="plain">
            <li>Status: {result.status}</li>
            <li>Apps still on: {result.appsEnabled.join(', ') || 'none'}</li>
            <li>Users who could still sign in: {result.peopleWithAccess}</li>
          </ul>
          <p className="muted small">Users and history are kept. To bring the company back, set it Active on the Sign-in tab and switch its apps on again.</p>
        </>
      ) : (
        <form className="form" onSubmit={submit}>
          <p>
            Offboarding switches off every app for {company.name} and marks it offboarded, so nobody in it can sign in.
            Users and history are kept. ICE then checks that it worked.
          </p>
          <Notice>{error}</Notice>
          <Field label={`Type ${company.slug} to confirm`}>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" />
          </Field>
          <div className="form-actions">
            <Button type="submit" variant="destructive" disabled={busy || confirm.trim() !== company.slug}>
              Offboard {company.name}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
